import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useGeocoder } from '../../hooks/useGeocoder';
import { reverseGeocode } from '../../utils/geocoding';
import './PlaceInput.css';

function contextText(place) {
  const parts = place.name.split(',').map(p => p.trim()).filter(Boolean);
  const primary = place.shortName.trim().toLowerCase();
  const rest = parts.filter(p => p.toLowerCase() !== primary);
  return rest.slice(0, 2).join(', ');
}

export function PlaceInput({ placeholder, value, pending, dotColor, label, onBadgeClick, onSelect, onClear }) {
  const { results, isSearching, search, clear } = useGeocoder();
  const [query, setQuery]         = useState(value || '');
  const [open, setOpen]           = useState(false);
  const [dropPos, setDropPos]     = useState({ top: 0, left: 0, width: 0 });
  const inputRef                  = useRef(null);
  const containerRef              = useRef(null);
  const dropdownRef               = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  // Recompute dropdown position from the input container's bounding rect
  const updatePos = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  // Keep position in sync while dropdown is open (handles sidebar scroll / window resize)
  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open, updatePos]);

  // Close on click outside both the input and the portalled dropdown
  useEffect(() => {
    const handler = (e) => {
      if (
        containerRef.current?.contains(e.target) ||
        dropdownRef.current?.contains(e.target)
      ) return;
      setOpen(false);
      clear();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [clear]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    search(val);
    if (!val) onClear?.();
  };

  const handleSelect = async (place) => {
    const name = await reverseGeocode(place.lat, place.lng) ?? place.shortName;
    setQuery(name);
    clear();
    setOpen(false);
    onSelect({ lat: place.lat, lng: place.lng, name });
  };

  const [gpsLoading, setGpsLoading] = useState(false);

  const handleGPS = () => {
    if (!navigator.geolocation || gpsLoading) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords;
        const name = await reverseGeocode(lat, lng) ?? 'My Location';
        setQuery(name);
        onSelect({ lat, lng, name });
        setGpsLoading(false);
      },
      () => setGpsLoading(false),
      { timeout: 10000 }
    );
  };

  const handleClear = () => {
    setQuery('');
    clear();
    setOpen(false);
    onClear?.();
    inputRef.current?.focus();
  };

  const dropdown = open && results.length > 0 && createPortal(
    <ul
      ref={dropdownRef}
      className="place-input__dropdown"
      style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width }}
      role="listbox"
    >
      {results.map(place => (
        <li
          key={place.id}
          className="place-input__option"
          onMouseDown={() => handleSelect(place)}
          role="option"
        >
          <svg className="place-input__pin" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span className="place-input__option-text">
            <span className="place-input__option-primary">{place.shortName}</span>
            {contextText(place) && (
              <span className="place-input__option-secondary">{contextText(place)}</span>
            )}
          </span>
        </li>
      ))}
    </ul>,
    document.body
  );

  return (
    <>
      <div className="place-input" ref={containerRef}>
        <div className="place-input__row">
          {label
            ? (
              <button
                type="button"
                className={`place-input__badge${onBadgeClick ? ' place-input__badge--clickable' : ''}`}
                style={{ background: dotColor }}
                onClick={onBadgeClick}
                disabled={!onBadgeClick}
                title={onBadgeClick ? 'Show on map' : undefined}
                tabIndex={-1}
              >{label}</button>
            )
            : <span className="place-input__dot" style={{ background: dotColor }} />
          }
          <input
            ref={inputRef}
            className="place-input__input"
            type="text"
            placeholder={pending ? 'Locating…' : placeholder}
            value={query}
            onChange={handleChange}
            onFocus={() => { if (query && results.length) { updatePos(); setOpen(true); } }}
            onKeyDown={e => e.key === 'Escape' && setOpen(false)}
            autoComplete="off"
            spellCheck={false}
          />
          <div className="place-input__controls">
            {isSearching && <span className="place-input__spinner" />}
            {!isSearching && query && (
              <button className="place-input__clear-btn" onClick={handleClear} title="Clear" tabIndex={-1} type="button">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/>
                </svg>
              </button>
            )}
            {/* A map-clicked waypoint is still resolving its snapped position/name
                — show that instead of the GPS button, which wouldn't make sense here. */}
            {!isSearching && !query && pending && <span className="place-input__spinner" title="Locating…" />}
            {!isSearching && !query && !pending && (
              <button
                className={`place-input__gps-btn${gpsLoading ? ' place-input__gps-btn--loading' : ''}`}
                onClick={handleGPS}
                title="Use my location"
                tabIndex={-1}
                type="button"
                disabled={gpsLoading}
              >
                {gpsLoading
                  ? <span className="place-input__spinner" />
                  : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                    </svg>
                  )
                }
              </button>
            )}
          </div>
        </div>
      </div>
      {dropdown}
    </>
  );
}
